import React from 'react';
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material';
import type { SocialPlatform } from '../../types/instagramAutomation.types';
import type { TiktokPostFields, YoutubePostFields } from './platformPostFields';
import { TikTokComposePanel } from './TikTokComposePanel';
import { IG_AUTO_GOLD, IG_AUTO_MUTED, IG_AUTO_WHITE, igAutoTextFieldSx } from './instagramAutomationTheme';

const selectSx = {
    bgcolor: '#201f1f',
    color: '#ffffff',
    borderRadius: 1,
    fontSize: 14,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 201, 101, 0.4)' },
    '& .MuiSvgIcon-root': { color: IG_AUTO_MUTED },
};

interface PlatformComposeFieldsProps {
    platform: SocialPlatform;
    sharedCaption: string;
    youtube?: YoutubePostFields;
    onYoutubeChange?: (patch: Partial<YoutubePostFields>) => void;
    tiktok?: TiktokPostFields;
    onTiktokChange?: (patch: Partial<TiktokPostFields>) => void;
    /** TikTok-only: load creator info + show compliance fields */
    tiktokActive?: boolean;
    tiktokMediaPreviewUrl?: string;
    tiktokMediaDurationSec?: number;
    tiktokCreatorNickname?: string;
}

export function PlatformComposeFields({
    platform,
    sharedCaption,
    youtube,
    onYoutubeChange,
    tiktok,
    onTiktokChange,
    tiktokActive = false,
    tiktokMediaPreviewUrl,
    tiktokMediaDurationSec,
    tiktokCreatorNickname,
}: PlatformComposeFieldsProps) {
    if (platform === 'youtube' && youtube && onYoutubeChange) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                <Typography sx={{ fontSize: 11, color: IG_AUTO_MUTED }}>
                    YouTube requires a title. Description defaults from the shared caption — edit as needed.
                </Typography>
                <TextField
                    label="Title *"
                    size="small"
                    value={youtube.title}
                    onChange={e => onYoutubeChange({ title: e.target.value })}
                    fullWidth
                    sx={igAutoTextFieldSx}
                />
                <TextField
                    label="Description"
                    size="small"
                    multiline
                    minRows={2}
                    value={youtube.description}
                    onChange={e => onYoutubeChange({ description: e.target.value })}
                    placeholder={sharedCaption || 'Video description'}
                    fullWidth
                    sx={igAutoTextFieldSx}
                />
                <TextField
                    label="Tags (comma-separated)"
                    size="small"
                    value={youtube.tags}
                    onChange={e => onYoutubeChange({ tags: e.target.value })}
                    fullWidth
                    sx={igAutoTextFieldSx}
                />
                <Box>
                    <Typography sx={{ fontSize: 10, color: IG_AUTO_MUTED, mb: 0.5 }}>Privacy</Typography>
                    <Select
                        size="small"
                        value={youtube.privacy_status}
                        onChange={e =>
                            onYoutubeChange({ privacy_status: e.target.value as YoutubePostFields['privacy_status'] })
                        }
                        fullWidth
                        sx={selectSx}>
                        <MenuItem value="public">Public</MenuItem>
                        <MenuItem value="unlisted">Unlisted</MenuItem>
                        <MenuItem value="private">Private</MenuItem>
                    </Select>
                </Box>
            </Box>
        );
    }

    if (platform === 'tiktok' && tiktok && onTiktokChange) {
        return (
            <TikTokComposePanel
                fields={tiktok}
                onChange={onTiktokChange}
                active={tiktokActive}
                mediaDurationSec={tiktokMediaDurationSec}
                mediaPreviewUrl={tiktokMediaPreviewUrl}
                creatorNicknameFallback={tiktokCreatorNickname}
                theme={{
                    muted: IG_AUTO_MUTED,
                    white: IG_AUTO_WHITE,
                    gold: IG_AUTO_GOLD,
                    red: '#ff7351',
                    mint: '#00fdc1',
                    surface: '#201f1f',
                    border: 'rgba(255,255,255,0.08)',
                }}
            />
        );
    }

    if (platform === 'instagram') {
        return (
            <Typography sx={{ fontSize: 11, color: IG_AUTO_MUTED, pt: 1 }}>
                Caption comes from the shared field above.
            </Typography>
        );
    }

    return null;
}
